/**
 * useAuth Hook - Manages user authentication state
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  checkAuthenticationStatus, 
  initiateUserLogin, 
  getCurrentUserInfo,
  refreshUserInfo,
  signOut as authSignOut 
} from '../services/user-auth-service';

// Function to update store appLogout flag (will be called from components that have store access)
let updateStoreAppLogout = null;

export const setStoreUpdater = (updater) => {
  updateStoreAppLogout = updater;
};

export const updateAppLogoutFlag = (isAuthenticated) => {
  if (updateStoreAppLogout) {
    updateStoreAppLogout({ appLogout: !isAuthenticated });
    console.log('Updated store appLogout flag:', !isAuthenticated);
  }
};

export const useAuth = () => {
  // Initialize state more intelligently
  const [user, setUser] = useState(() => {
    // Try to get cached user info immediately on initialization
    try {
      const storedUserInfo = sessionStorage.getItem('userInfo');
      return storedUserInfo ? JSON.parse(storedUserInfo) : null;
    } catch (error) {
      console.warn('Failed to parse cached user info on init:', error);
      return null;
    }
  });
  
  const [isLoading, setIsLoading] = useState(() => {
    // If we have cached user info and token, start as not loading
    const hasToken = sessionStorage.getItem('authToken');
    const hasUserInfo = sessionStorage.getItem('userInfo');
    return !(hasToken && hasUserInfo);
  });
  
  const [isAuthenticated, setIsAuthenticatedState] = useState(() => {
    // Initialize authentication state based on cached data
    const hasToken = sessionStorage.getItem('authToken');
    const hasUserInfo = sessionStorage.getItem('userInfo');
    return !!(hasToken && hasUserInfo);
  });

  // Wrapper function to update both state and store
  const setIsAuthenticated = useCallback((value) => {
    console.log('Setting authentication state to:', value);
    setIsAuthenticatedState(value);
    updateAppLogoutFlag(value);
  }, []);
  
  const [error, setError] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [lastAuthTime, setLastAuthTime] = useState(() => {
    // Check if user was recently authenticated
    const justAuth = sessionStorage.getItem('justAuthenticated');
    return justAuth ? Date.now() : 0;
  });

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const storedToken = sessionStorage.getItem('authToken');
        const storedUserInfo = sessionStorage.getItem('userInfo');
        
        if (!storedToken || !storedUserInfo) {
          console.log('No valid stored authentication found');
          setIsAuthenticated(false);
          setUser(null);
          setIsLoading(false);
          return;
        }

        // Parse and set user info
        try {
          const userInfo = JSON.parse(storedUserInfo);
          setUser(userInfo);
          setIsAuthenticated(true);
          updateAppLogoutFlag(true); // Mark as authenticated in store
          console.log('User authenticated with stored credentials:', userInfo.name);
        } catch (parseError) {
          console.error('Failed to parse stored user info:', parseError);
          sessionStorage.removeItem('userInfo');
          sessionStorage.removeItem('authToken');
          setIsAuthenticated(false);
          setUser(null);
          updateAppLogoutFlag(false);
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Error during authentication check:', error);
        setError(error.message);
        setIsAuthenticated(false);
        setUser(null);
        updateAppLogoutFlag(false);
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Listen for auth state changes to update last auth time
  useEffect(() => {
    const handleAuthStateChange = () => {
      const justAuth = sessionStorage.getItem('justAuthenticated');
      if (justAuth) {
        setLastAuthTime(Date.now());
        console.log('Auth state change detected, updating last auth time');
      }
      
      // Also refresh user data from storage and sync store
      const storedUserInfo = sessionStorage.getItem('userInfo');
      const storedToken = sessionStorage.getItem('authToken');
      
      if (storedUserInfo && storedToken) {
        try {
          const userInfo = JSON.parse(storedUserInfo);
          setUser(userInfo);
          setIsAuthenticated(true);
          updateAppLogoutFlag(true); // Ensure store is updated
          console.log('User data refreshed after auth state change:', userInfo.name);
        } catch (error) {
          console.error('Failed to parse user info after auth state change:', error);
          setIsAuthenticated(false);
          setUser(null);
          updateAppLogoutFlag(false);
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
        updateAppLogoutFlag(false);
      }
    };

    const handlePhotoUpdate = () => {
      console.log('Photo update detected, refreshing user data');
      const storedUserInfo = sessionStorage.getItem('userInfo');
      if (storedUserInfo) {
        try {
          const userInfo = JSON.parse(storedUserInfo);
          setUser(userInfo);
          console.log('User data updated with new photo:', userInfo.photo ? 'Photo available' : 'No photo');
        } catch (error) {
          console.error('Failed to parse user info after photo update:', error);
        }
      }
    };

    window.addEventListener('authStateChanged', handleAuthStateChange);
    window.addEventListener('userPhotoUpdated', handlePhotoUpdate);
    
    return () => {
      window.removeEventListener('authStateChanged', handleAuthStateChange);
      window.removeEventListener('userPhotoUpdated', handlePhotoUpdate);
    };
  }, []);

  // Login function
  const login = useCallback(() => {
    try {
      setError(null);
      initiateUserLogin();
    } catch (error) {
      console.error('Error initiating login:', error);
      setError(error.message);
    }
  }, []);

  // Refresh user info
  const refresh = useCallback(async () => {
    try {
      setError(null);
      
      console.log('Refreshing authentication state...');
      
      // First check if we have stored tokens and user info
      const storedToken = sessionStorage.getItem('authToken');
      const storedUserInfo = sessionStorage.getItem('userInfo');
      
      if (!storedToken) {
        console.log('No stored token found during refresh');
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
        return null;
      }
      
      // If we have both token and user info, set them immediately
      if (storedUserInfo) {
        try {
          const parsedUserInfo = JSON.parse(storedUserInfo);
          console.log('Setting user info from cache during refresh:', parsedUserInfo);
          setUser(parsedUserInfo);
          setIsAuthenticated(true);
          setIsLoading(false);
          
          // Validate in background but don't block the refresh
          setTimeout(async () => {
            try {
              const authStatus = await checkAuthenticationStatus();
              if (!authStatus.isAuthenticated) {
                console.log('Background validation failed, clearing auth state');
                setUser(null);
                setIsAuthenticated(false);
                sessionStorage.removeItem('authToken');
                sessionStorage.removeItem('userInfo');
              }
            } catch (error) {
              console.warn('Background validation error:', error);
            }
          }, 500);
          
          return parsedUserInfo;
        } catch (error) {
          console.warn('Failed to parse stored user info:', error);
        }
      }
      
      console.log('No cached user info, checking authentication status...');
      setIsLoading(true);
      
      // Check authentication status with the backend
      const authStatus = await checkAuthenticationStatus();
      
      if (authStatus.isAuthenticated && authStatus.userInfo) {
        console.log('Authentication confirmed, updating state...');
        setUser(authStatus.userInfo);
        setIsAuthenticated(true);
        setIsLoading(false);
        return authStatus.userInfo;
      } else {
        console.log('Authentication check failed during refresh');
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
        return null;
      }
    } catch (error) {
      console.error('Error refreshing user info:', error);
      setError(error.message);
      
      // Check if we have cached user info as fallback
      const storedUserInfo = sessionStorage.getItem('userInfo');
      if (storedUserInfo) {
        try {
          const parsedUserInfo = JSON.parse(storedUserInfo);
          console.log('Using cached user info as fallback after refresh error');
          setUser(parsedUserInfo);
          setIsAuthenticated(true);
          setIsLoading(false);
          return parsedUserInfo;
        } catch (parseError) {
          console.error('Failed to parse cached user info:', parseError);
        }
      }
      
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
      return null;
    }
  }, []);

  // Sign out function
  const signOut = useCallback(() => {
    try {
      setError(null);
      authSignOut();
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Error signing out:', error);
      setError(error.message);
    }
  }, []);

  // Get current user synchronously
  const getCurrentUser = useCallback(() => {
    return getCurrentUserInfo();
  }, []);

  // Refresh user photo specifically  
  const refreshUserPhoto = useCallback(async () => {
    try {
      console.log('Refreshing user photo...');
      const token = sessionStorage.getItem('authToken');
      const storedUserInfo = sessionStorage.getItem('userInfo');
      
      if (!token || !storedUserInfo) {
        console.log('No token or user info available for photo refresh');
        return false;
      }

      // Import the fetchUserPhoto function
      const { fetchUserPhoto } = await import('../services/auth-code-service');
      const photo = await fetchUserPhoto(token);
      
      if (photo) {
        // Update user info with new photo
        const userInfo = JSON.parse(storedUserInfo);
        userInfo.photo = photo;
        
        // Update both state and storage
        setUser(userInfo);
        sessionStorage.setItem('userInfo', JSON.stringify(userInfo));
        
        console.log('User photo refreshed successfully');
        return true;
      } else {
        console.log('No photo available or fetch failed');
        return false;
      }
    } catch (error) {
      console.error('Error refreshing user photo:', error);
      return false;
    }
  }, []);

  return {
    user,
    userInfo: user, // Alias for compatibility
    token: sessionStorage.getItem('authToken'),
    isLoading,
    isVerifying,
    isAuthenticated,
    error,
    login,
    signOut,
    logout: signOut, // Alias for compatibility  
    refresh,
    refreshUserPhoto,
    getCurrentUser
  };
};
